import { SECTION_IDS } from "@/hooks/useArtifactsViewerData";
import { useEffect, useRef, useState } from "react";

const ALL_SECTION_IDS = Object.values(SECTION_IDS);
const SECTION_IDS_SET = new Set<string>(ALL_SECTION_IDS);

/**
 * 监听滚动，检测当前视口中最显眼的 Section
 * 使用 IntersectionObserver 检测哪个 section 在视口中占比最大
 * 使用 MutationObserver 监听 DOM 变化，处理动态加载的 section
 *
 * 性能优化：
 * 1. 防抖更新机制，避免频繁重渲染
 * 2. 只在 activeSectionId 真正变化时更新状态
 * 3. 优化 MutationObserver 的查询逻辑
 * 4. 批量处理 DOM 变化
 */
export function useActiveSection(
  scrollContainer: HTMLElement | null
): string | null {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // 使用 ref 存储当前值，避免闭包陈旧问题
  const activeSectionIdRef = useRef<string | null>(null);
  const updateTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!scrollContainer) return;

    // 存储每个 section 的可见比例
    const visibilityMap = new Map<string, number>();
    // 记录已观察的元素，避免重复观察
    const observedElements = new Set<Element>();

    // 防抖更新函数：避免在快速滚动时频繁更新状态
    const scheduleUpdate = (newId: string | null) => {
      if (updateTimerRef.current !== null) {
        cancelAnimationFrame(updateTimerRef.current);
      }

      updateTimerRef.current = requestAnimationFrame(() => {
        // 只在真正变化时更新状态
        if (activeSectionIdRef.current !== newId) {
          activeSectionIdRef.current = newId;
          setActiveSectionId(newId);
        }
        updateTimerRef.current = null;
      });
    };

    // 计算最大可见 section - 提取为独立函数
    const updateActiveSection = () => {
      let maxRatio = 0;
      let maxId: string | null = null;

      // 使用 for...of 替代 forEach，性能稍好
      for (const [id, ratio] of visibilityMap) {
        if (ratio > maxRatio) {
          maxRatio = ratio;
          maxId = id;
        }
      }

      // 只有当有 section 可见时才更新
      if (maxId && maxRatio > 0) {
        scheduleUpdate(maxId);
      }
    };

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        // 批量更新 visibilityMap
        let hasUpdate = false;
        for (const entry of entries) {
          const id = entry.target.id;
          if (SECTION_IDS_SET.has(id)) {
            const oldRatio = visibilityMap.get(id);
            const newRatio = entry.intersectionRatio;
            // 只有当比例真正变化时才更新
            if (oldRatio !== newRatio) {
              visibilityMap.set(id, newRatio);
              hasUpdate = true;
            }
          }
        }

        // 只有当有更新时才重新计算
        if (hasUpdate) {
          updateActiveSection();
        }
      },
      {
        root: scrollContainer,
        // 减少 threshold 数量，降低回调频率但保持足够精度
        threshold: [0, 0.25, 0.5, 0.75, 1],
        // rootMargin 让检测更靠近视口顶部
        rootMargin: "-10% 0px -70% 0px",
      }
    );

    // 观察指定 id 的元素
    const observeElement = (id: string) => {
      const element = document.getElementById(id);
      if (element && !observedElements.has(element)) {
        intersectionObserver.observe(element);
        observedElements.add(element);
      }
    };

    // 初始化：观察已存在的 section 元素
    ALL_SECTION_IDS.forEach(observeElement);

    // 使用 MutationObserver 监听 DOM 变化，处理动态加载的 section
    // 优化：使用更高效的查询策略
    const mutationObserver = new MutationObserver((mutations) => {
      // 收集所有新增的节点，批量处理
      const nodesToCheck = new Set<HTMLElement>();

      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              nodesToCheck.add(node);
            }
          });
        }
      }

      // 批量检查和观察
      for (const node of nodesToCheck) {
        // 检查新增节点本身是否是目标 section
        if (node.id && SECTION_IDS_SET.has(node.id)) {
          observeElement(node.id);
        }

        // 优化：使用 querySelectorAll 一次性查询所有目标，而非逐个查询
        // 构建 CSS 选择器：#section1, #section2, ...
        const selector = ALL_SECTION_IDS.map((id) => `#${id}`).join(", ");
        const foundElements = node.querySelectorAll(selector);

        foundElements.forEach((element) => {
          if (element.id && SECTION_IDS_SET.has(element.id)) {
            observeElement(element.id);
          }
        });
      }
    });

    // 在 scrollContainer 上监听子树变化
    mutationObserver.observe(scrollContainer, {
      childList: true,
      subtree: true,
    });

    return () => {
      // 清理防抖 timer
      if (updateTimerRef.current !== null) {
        cancelAnimationFrame(updateTimerRef.current);
      }
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [scrollContainer]);

  return activeSectionId;
}
